import { useState, useEffect, useCallback } from 'react'
import type { GameItem } from '../../i18n'
import { type Card, type GameMode, type Difficulty, generateCards, DIFFICULTY_PAIRS } from './gameData'

export interface GameState {
  cards: Card[]
  selectedIds: number[]       // currently face-up (unmatched) card ids
  matchedPairIds: Set<number>
  moves: number
  startTime: number | null
  endTime: number | null
  isLocked: boolean           // prevent clicks while animating
  gameComplete: boolean
}

function initState(mode: GameMode, difficulty: Difficulty, items: GameItem[]): GameState {
  return {
    cards: generateCards(mode, difficulty, items),
    selectedIds: [],
    matchedPairIds: new Set(),
    moves: 0,
    startTime: null,
    endTime: null,
    isLocked: false,
    gameComplete: false,
  }
}

export function useMemoryGame(mode: GameMode, difficulty: Difficulty, items: GameItem[]) {
  const [state, setState] = useState<GameState>(() => initState(mode, difficulty, items))

  const resetGame = useCallback(() => {
    setState(initState(mode, difficulty, items))
  }, [mode, difficulty, items])

  // After a mismatch, flip both cards back after 900 ms
  useEffect(() => {
    if (!state.isLocked || state.selectedIds.length !== 2) return

    const timer = setTimeout(() => {
      setState((prev) => {
        const [idA, idB] = prev.selectedIds
        return {
          ...prev,
          cards: prev.cards.map((c) =>
            c.id === idA || c.id === idB ? { ...c, isFlipped: false } : c
          ),
          selectedIds: [],
          isLocked: false,
        }
      })
    }, 900)

    return () => clearTimeout(timer)
  }, [state.isLocked, state.selectedIds])

  const flipCard = useCallback((cardId: number) => {
    setState((prev) => {
      if (prev.isLocked) return prev

      const card = prev.cards.find((c) => c.id === cardId)
      if (!card || card.isFlipped || card.isMatched) return prev
      if (prev.selectedIds.length >= 2) return prev

      const newCards = prev.cards.map((c) =>
        c.id === cardId ? { ...c, isFlipped: true } : c
      )
      const newSelected = [...prev.selectedIds, cardId]
      const startTime = prev.startTime ?? Date.now()

      // First card of a pair — just flip
      if (newSelected.length === 1) {
        return { ...prev, cards: newCards, selectedIds: newSelected, startTime }
      }

      // Second card — check for match
      const [idA, idB] = newSelected
      const cardA = newCards.find((c) => c.id === idA)
      const cardB = newCards.find((c) => c.id === idB)
      if (!cardA || !cardB) return prev
      const newMoves = prev.moves + 1

      if (cardA.pairId === cardB.pairId) {
        const matchedCards = newCards.map((c) =>
          c.pairId === cardA.pairId ? { ...c, isMatched: true } : c
        )
        const newMatched = new Set(prev.matchedPairIds)
        newMatched.add(cardA.pairId)
        const gameComplete = newMatched.size === DIFFICULTY_PAIRS[difficulty]

        return {
          ...prev,
          cards: matchedCards,
          selectedIds: [],
          matchedPairIds: newMatched,
          moves: newMoves,
          startTime,
          isLocked: false,
          gameComplete,
          endTime: gameComplete ? Date.now() : prev.endTime,
        }
      }

      // No match — lock and schedule flip-back
      return {
        ...prev,
        cards: newCards,
        selectedIds: newSelected,
        moves: newMoves,
        startTime,
        isLocked: true,
      }
    })
  }, [difficulty])

  return { state, flipCard, resetGame }
}
